import { Box, Link, Stack, Typography } from '@mui/material';
import OutreachTypeAvatar from 'src/components/outreach-type-avatar/OutreachTypeAvatar';
import Label from 'src/components/label';
import CanvassDistanceAlert from './CanvassDistanceAlert';
import { ActivityFeedRow, ActivityType } from 'src/@types/reports';
import { fName } from 'src/utils/formatName';
import { fDateTime } from 'src/utils/formatTime';
import PaidInteractionAlert from './PaidInteractionAlert';
import { formatResponseCode } from 'src/utils/formatResponseCode';

type Props = {
  activityFeedRow: ActivityFeedRow;
  voterName?: string;
  onVoterClick?: (row: ActivityFeedRow) => void;
};

export default function ActivityItem({ activityFeedRow, voterName, onVoterClick }: Props) {
  const type = activityFeedRow.type || '';
  const tagName = activityFeedRow.tag_name;
  const tagValue = activityFeedRow.tag_value || '';
  const notes = activityFeedRow.notes;
  const date = fDateTime(activityFeedRow.created_at);
  const disposition = activityFeedRow.disposition || 'No Data';

  return (
    <Stack direction="row">
      <Stack alignItems="center">
        <Stack marginBottom={1} marginTop={1}>
          <OutreachTypeAvatar size={28} outreachType={getOutreachType(type)} />
        </Stack>
        <Stack sx={{ width: 2, backgroundColor: (theme) => theme.palette.divider, flexGrow: 1 }} />
      </Stack>

      <Box px={2} width="100%" pb={1} marginBottom={1} marginTop={1}>
        {shouldShowDisposition(activityFeedRow) && (
          <Box mb={0.5}>
            <Label color="default" variant="outlined">
              {formatResponseCode(disposition)}
            </Label>
          </Box>
        )}

        <Typography variant="caption">
          {getLabel(activityFeedRow, voterName, onVoterClick)}
        </Typography>
        <br />

        {type == ActivityType.CANVASS && <CanvassDistanceAlert activityFeedRow={activityFeedRow} />}
        {activityFeedRow.is_paid_interaction && (
          <PaidInteractionAlert activityFeedRow={activityFeedRow} />
        )}
        {notes && (
          <Box p={1} px={2} mt={1} sx={{ borderRadius: 1, backgroundColor: 'grey.200' }}>
            <Typography variant="caption">{notes}</Typography>
          </Box>
        )}
        {tagName && tagValue && (
          <Box p={1} px={2} mt={1} sx={{ borderRadius: 1, backgroundColor: 'grey.200' }}>
            <Typography variant="caption">
              {tagName}: <b>{tagValue}</b>
            </Typography>
          </Box>
        )}

        <Box>
          <Typography variant="caption" sx={{ mt: 1 }}>
            {date}
          </Typography>
        </Box>
      </Box>
    </Stack>
  );
}

const getLabel = (
  activityFeedRow: ActivityFeedRow,
  voterName?: string,
  onVoterClick?: (row: ActivityFeedRow) => void
) => {
  if (activityFeedRow.is_inbound) {
    return getVoterResponseActivityLabel(activityFeedRow, voterName, onVoterClick);
  } else return getUserActivityLabel(activityFeedRow, voterName, onVoterClick);
};

const getUserActivityLabel = (
  activityFeedRow: ActivityFeedRow,
  voterName?: string,
  onVoterClick?: (row: ActivityFeedRow) => void
) => {
  const type = activityFeedRow.type || '';
  const voter =
    voterName ||
    fName(activityFeedRow.voter_first_name || '', '', activityFeedRow.voter_last_name || '');
  let user = fName(activityFeedRow.user_first_name || '', '', activityFeedRow.user_last_name || '');

  if (!user) user = 'Unknown User';

  const voterElement =
    onVoterClick &&
    (activityFeedRow.type === ActivityType.CALL ||
      activityFeedRow.type === ActivityType.CANVASS) ? (
      <Link
        component="button"
        variant="caption"
        sx={{ fontWeight: 'bold', verticalAlign: 'baseline' }}
        onClick={() => onVoterClick(activityFeedRow)}
      >
        {voter}
      </Link>
    ) : (
      <b>{voter}</b>
    );

  return (
    <>
      <b>{user}</b> {getOutreachLabel(type)} {voterElement}
    </>
  );
};

const getVoterResponseActivityLabel = (
  activityFeedRow: ActivityFeedRow,
  voterName?: string,
  onVoterClick?: (row: ActivityFeedRow) => void
) => {
  const voter =
    voterName ||
    fName(activityFeedRow.voter_first_name || '', '', activityFeedRow.voter_last_name || '');

  const voterElement =
    onVoterClick &&
    (activityFeedRow.type === ActivityType.CALL ||
      activityFeedRow.type === ActivityType.CANVASS) ? (
      <Link
        component="button"
        variant="caption"
        sx={{ fontWeight: 'bold', verticalAlign: 'baseline' }}
        onClick={() => onVoterClick(activityFeedRow)}
      >
        {voter}
      </Link>
    ) : (
      <b>{voter}</b>
    );

  return (
    <>
      {voterElement} {getVoterResponseLabel(activityFeedRow.type || '')}
    </>
  );
};

const shouldShowDisposition = (activityFeedRow: ActivityFeedRow): boolean => {
  const { disposition, type } = activityFeedRow;

  return !!disposition && (type === ActivityType.CALL || type === ActivityType.CANVASS);
};

const getOutreachType = (type: string) => {
  switch (type) {
    case ActivityType.CALL:
      return 'call';
    case ActivityType.TEXT:
      return 'text';
    case ActivityType.SURVEY:
      return 'text-survey';
    case ActivityType.CANVASS:
      return 'canvass';
    case ActivityType.TAG:
      return 'tag';
    case ActivityType.NOTES:
      return 'notes';
    case ActivityType.RELATIONAL:
      return 'relational';
    default:
      return null;
  }
};

const getOutreachLabel = (type: string) => {
  switch (type) {
    case ActivityType.CALL:
      return 'called';
    case ActivityType.TEXT:
      return 'texted';
    case ActivityType.SURVEY:
      return 'surveyed';
    case ActivityType.CANVASS:
      return 'canvassed';
    case ActivityType.TAG:
      return 'tagged';
    case ActivityType.NOTES:
      return 'updated notes for';
    case ActivityType.RELATIONAL:
      return 'contacted';
    default:
      return null;
  }
};

const getVoterResponseLabel = (type: string) => {
  switch (type) {
    case ActivityType.CALL:
      return 'answered call';
    case ActivityType.TEXT:
      return 'responded to text';
    case ActivityType.SURVEY:
      return 'completed survey';
    case ActivityType.CANVASS:
      return 'answered door';
    case ActivityType.TAG:
      return 'had tag added';
    case ActivityType.NOTES:
      return 'had notes added';
    case ActivityType.RELATIONAL:
      return 'responded to relational contact';
    default:
      return null;
  }
};
